<template>
	<div v-if="invoice" class="payment-methods-container">
		<ul v-if="loading === false">
			<li v-for="(item, index) in filteredPaymentMethods" :key="index" :class="{ active: item.open }">
				<div :class="{ header: true, active: item.open }" @click="toggleItem(index)">
					<div v-if="item.icons.length > 0" class="payment-method-icon-container">
						<img v-for="icon in item.icons" :key="icon" :src="require(`@/assets/checkout/${icon}.svg`)"
							:alt="icon">
					</div>
					{{ item.title }}
				</div>
				<div v-show="item.open" class="content">
					<component :is="item.component" :invoice="invoice" :braintree-instance="braintreeInstance"
						:device-data="deviceData" :disable-payment-method="disablePaymentMethod"
						:show-loading-message="showLoadingMessage" :show-error="showError" />
				</div>
			</li>
		</ul>
		<div v-else-if="loading === true" class="centered marginTop">
			<div class="loading" />
		</div>
	</div>
</template>

<script>
import ApplePayComponent from './PaymentMethods/ApplePayComponent.vue'
import CreditCardComponent from './PaymentMethods/CreditCardComponent.vue'
import ECheckComponent from './PaymentMethods/ECheckComponent.vue'
import GooglePayComponent from './PaymentMethods/GooglePayComponent.vue'
import PayPalComponent from './PaymentMethods/PayPalComponent.vue'
import VenmoComponent from './PaymentMethods/VenmoComponent.vue'
import WireTransferComponent from './PaymentMethods/WireTransferComponent.vue'
import ZelleComponent from './PaymentMethods/ZelleComponent.vue'
// Mixins
import InvoiceValidateMixin from '../../../mixins/InvoiceValidateMixin.js'
import PaymentMethodMixin from '../../../mixins/PaymentMethodMixin.js'
// Errors
import ApiError from '../../../errors/ApiError.js'
import BraintreeError from '../../../errors/BraintreeError.js'

export default {
	name: 'PaymentMethodListComponent',
	components: {
	},
	mixins: [InvoiceValidateMixin, PaymentMethodMixin],
	props: ['invoice', 'showLoadingMessage', 'showError'],
	data() {
		return {
			braintreeInstance: null,
			loading: true,
			error: '',
			paymentMethods: [
				{
					title: 'Credit Card',
					component: CreditCardComponent,
					open: false,
					icons: ['visa', 'mastercard', 'amex'],
					shouldShow: this.canUseCreditCard,
					disabled: false,
					type: 'credit_card'
				},
				{
					title: 'Google Pay',
					component: GooglePayComponent,
					open: false,
					icons: ['google-pay'],
					shouldShow: this.canUseGooglePay,
					disabled: false,
					type: 'google_pay'
				},
				{
					title: 'Apple Pay',
					component: ApplePayComponent,
					open: false,
					icons: ['apple-pay'],
					shouldShow: this.canUseApplePay,
					disabled: false,
					type: 'apple_pay'
				},
				{
					title: 'PayPal',
					component: PayPalComponent,
					open: false,
					icons: ['paypal'],
					shouldShow: this.canUsePayPal,
					disabled: false,
					type: 'paypal'
				},
				{
					title: 'Venmo',
					component: VenmoComponent,
					open: false,
					icons: ['venmo'],
					shouldShow: this.canUseVenmo,
					disabled: false,
					type: 'venmo'
				},
				{
					title: 'Zelle',
					component: ZelleComponent,
					open: false,
					icons: ['zelle'],
					shouldShow: this.canUseZellePayment,
					disabled: false,
					type: 'zelle'
				},
				{
					title: 'E-Check',
					component: ECheckComponent,
					open: false,
					icons: ['e-check'],
					shouldShow: this.canUseECheck,
					disabled: false,
					type: 'e_check'
				},
				{
					title: 'Wire Transfer',
					component: WireTransferComponent,
					open: false,
					icons: ['wire-transfer'],
					shouldShow: this.canUseWireTransfer,
					disabled: false,
					type: 'wire_transfer'
				}
			],
			creditCardPaymentRestricted: true,
			dataCollectorInstance: null,
			deviceData: null
		}
	},
	computed: {
		filteredPaymentMethods() {
			return this.paymentMethods.filter(
				(item) => item.shouldShow() && !item.disabled
			)
		},
		paymentFormStore() {
			return this.$store.getters.paymentForm
		}
	},
	created() {
		this.validateInvoice(this.invoice)
		this.init()
	},
	methods: {
		/**
	 * Create a braintree instance.
	 * @param {string} clientToken
	 */
		createBraintreeInstance: function (clientToken) {
			const self = this
			return window.braintree.client
				.create({
					authorization: clientToken
				})
				.then(async instance => {
					self.braintreeInstance = instance
					await self.getDeviceData()
				})
				.catch((e) => {
					const exception = new BraintreeError(e)
					console.error(exception.getMessage())
					return null
				})
		},
		/**
	 * Get a client token from server.
	 * @returns {Promise<string>} clientToken
	 */
		getClientToken: async function () {
			var apiPayload = {}
			apiPayload.apiEndpoint = 'shipping'
			apiPayload.postData = {}
			apiPayload.postData.action = 'braintree_client_token'
			apiPayload.postData.id = this.invoice.customer_id
			const response = await this.$store.dispatch('apiCall', apiPayload).catch(err => {
				const exception = new ApiError(err)
				console.error(exception.getMessage())
			})
			const clientToken = response.data.payload.clientToken
			if (!clientToken || !clientToken.length) {
				const exception = new ApiError(new Error('Get client token error'))
				this.error = exception.getMessage()
				return
			}
			return clientToken
		},
		/**
	 * Get device data
	 *
	 * It is recommended that all customer-initiated transactions include
	 * device data. Device data increases the accuracy of our available
	 * Premium Fraud Management Tools in determining when a transaction is
	 * fraudulent.
	 */
		getDeviceData: async function () {
			this.dataCollectorInstance = await window.braintree.dataCollector
				.create({
					client: this.braintreeInstance
				})
				.catch((err) => {
					const exception = new BraintreeError(err)
					console.error('Get device data error', exception.getMessage())
				})
			if (!this.dataCollectorInstance) {
				// We failed to get device data for some reason. This is not fatal, but it is recommended.
				console.error('Get device data error')
				return
			}
			// At this point, you should access the dataCollectorInstance.deviceData value and provide it
			// to your server, e.g. by injecting it into your form as a hidden input.
			this.deviceData = this.dataCollectorInstance.deviceData
		},
		/**
	 * Initialize the component.
	 * Load the client token and create a braintree instance.
	 */
		init: async function () {
			this.loading = true
			const clientToken = await this.getClientToken()
			if (!clientToken) {
				this.error = 'Get client token error'
				this.loading = false
				return
			}
			await this.createBraintreeInstance(clientToken)
			this.loading = false
		},
		addConvenienceFee: async function () {
			this.loading = true
			var apiPayload = {}
			apiPayload.apiEndpoint = 'account'
			apiPayload.postData = {}
			apiPayload.postData.action = 'add_convenience_fee'
			apiPayload.postData.invoice_id = this.invoice.invoice_id
			const response = await this.$store.dispatch('apiCall', apiPayload).catch(err => {
				const exception = new ApiError(err)
				console.error(exception.getMessage())
				this.loading = false
			})
			this.loading = false
			if (response.data.result === 'success') {
				this.$store.dispatch('updateInvoices', response.data.payload)
			}
		},
		removeConvienceFee: async function () {
			this.loading = true
			var apiPayload = {}
			apiPayload.apiEndpoint = 'account'
			apiPayload.postData = {}
			apiPayload.postData.action = 'remove_convenience_fee'
			apiPayload.postData.invoice_id = this.invoice.invoice_id
			const response = await this.$store.dispatch('apiCall', apiPayload).catch(err => {
				const exception = new ApiError(err)
				console.error(exception.getMessage())
				this.loading = false
			})
			this.loading = false
			if (response.data.result === 'success') {
				this.$store.dispatch('updateInvoices', response.data.payload)
			}
		},

		/**
	 * Open/close accordion item
	 * @param {*} index
	 */
		toggleItem: function (index) {
			var self = this

			this.filteredPaymentMethods.forEach(async (item, i) => {
				if (index !== i) {
					item.open = false
					return
				}
				this.filteredPaymentMethods[index].open =
					!this.filteredPaymentMethods[index].open

				const paymentForm = {
					nonce: null,
					type: this.filteredPaymentMethods[index].type,
					submitCC: false,
					isPartialPayment: false,
					amount: 0
				}

				if (this.filteredPaymentMethods[index].open === false) {
					paymentForm.type = null
				}

				// add convenience fee, if it hasn't alredy been added
				if (paymentForm.type === 'credit_card' || paymentForm.type === 'paypal' || paymentForm.type === 'venmo' || paymentForm.type === 'google_pay' || paymentForm.type === 'apple_pay') {
					if (self.invoice.invoice_items.some(item => item.convenience_fee === 'y' && item.description === 'Convenience Fee') === false) {
						await self.addConvenienceFee()
					}
				} else { // remove convenience fee, if one exists
					if (self.invoice.invoice_items.some(item => item.convenience_fee === 'y' && item.description === 'Convenience Fee') === true) {
						await self.removeConvienceFee()
					}
				}

				self.$store.dispatch('updatePaymentForm', paymentForm)
			})
		}
	}
}
</script>
<style scoped>
ul {
	margin-bottom: 0;
}

li {
	list-style-type: none;
	margin-bottom: 0;
}

li:first-child .header {
	border-radius: 0.5em 0.5em 0 0;
}

li:last-child .header {
	border-bottom: 1px solid rgba(51, 51, 51, 0.5);
	border-radius: 0 0 0.5em 0.5em;
}

li:last-child .content {
	border-bottom: 1px solid rgba(51, 51, 51, 0.5);
	border-radius: 0 0 0.5em 0.5em;
}

li:last-child .header.active {
	border-bottom: none;
	border-radius: 0;
}

.header {
	cursor: pointer;
	background-color: lightgray;
	padding: 10px;
	border: 1px solid rgba(51, 51, 51, 0.5);
	border-bottom: none;
	background: var(--off-white, #f5f7fa);
	display: flex;
	flex-direction: row;
	align-items: center;
}

.header.active {
	background: var(--slate-blue-lightest, #b5c3d9);
}

.content {
	background: var(--white, #fff);
	padding: 10px;
	border-top: 1px solid rgba(51, 51, 51, 0.5);
	border-left: 1px solid rgba(51, 51, 51, 0.5);
	border-right: 1px solid rgba(51, 51, 51, 0.5);
}

.payment-method-icon-container {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	margin-right: 1em;
}

.payment-method-icon-container img {
	width: 2.875em;
	height: 2em;
}

.loading {
	margin-top: 1em;
	margin-left: auto;
	margin-right: auto;
}
</style>
